/**
 * Explains Box synchronization and exposes its shared connector cadence.
 *
 * One-time imports deliberately persist a manual feed, while synchronized
 * folders retain the existing scheduled feed contract and incremental-copy
 * behavior owned by the backend.
 */
import { useState } from 'react'
import { ChevronRight } from 'lucide-react'

import { ConnectorScheduleField } from '../../components/ConnectorScheduleSelect'
import type { ConnectorSchedule } from '../../types'
import { cn } from '@/lib/utils'

export function BoxSyncOptions({
  schedule,
  onScheduleChange,
}: {
  schedule: ConnectorSchedule
  onScheduleChange: (schedule: ConnectorSchedule) => void
}) {
  const [showExplanation, setShowExplanation] = useState(false)
  const oneTime = schedule === 'manual'

  return (
    <div className="space-y-3">
      {oneTime
        ? (
            <p className="text-xs leading-4 text-slate-500">
              One-time import — nothing is scheduled. Switch to “Keep folders in sync” above to pick up new and changed files automatically.
            </p>
          )
        : (
            <div className="[&>div]:flex [&>div]:items-center [&>div]:gap-3 [&>div]:space-y-0 [&_[data-slot=select-trigger]]:h-8 [&_[data-slot=select-trigger]]:w-[218px] [&_p]:hidden">
              <ConnectorScheduleField
                value={schedule}
                onChange={onScheduleChange}
                ariaLabel="Box connector schedule"
              />
            </div>
          )}

      <button
        type="button"
        className="flex items-center gap-1 rounded-sm text-xs font-medium text-slate-600 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        aria-expanded={showExplanation}
        onClick={() => setShowExplanation(current => !current)}
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-150',
            showExplanation && 'rotate-90',
          )}
        />
        How syncing works
      </button>

      {showExplanation && (
        <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/70 p-3 text-xs leading-4 text-slate-600">
          <p>
            Synced folders are re-checked on the schedule; only new and changed files are copied in, and the newest copy is kept when a file is replaced.
          </p>
          <p>
            Historical files enter the configured review workflow on the first sync. Scheduled runs then import only new or changed files.
          </p>
        </div>
      )}
    </div>
  )
}
